import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Upload,
  Trash2,
  Search,
  RefreshCw,
  Database,
  CheckCircle,
  AlertCircle,
  Clock,
  Loader,
  FileCode,
  FileType,
  File,
} from 'lucide-react';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import api, { Datasource, KnowledgeBaseStats } from '../../../services/api';
import { DocumentUploadModal } from '../DocumentUploadModal';
import styles from './KnowledgeBase.module.css';

export interface KnowledgeBaseProps {
  assistantId?: string;
  assistantName?: string;
}

const getFileIcon = (fileType?: string) => {
  switch (fileType) {
    case 'pdf':
      return <FileText size={20} className={styles.iconPdf} />;
    case 'code':
      return <FileCode size={20} className={styles.iconCode} />;
    case 'markdown':
      return <FileType size={20} className={styles.iconMarkdown} />;
    default:
      return <File size={20} className={styles.iconDefault} />;
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'complete':
      return <CheckCircle size={16} className={styles.statusComplete} />;
    case 'error':
      return <AlertCircle size={16} className={styles.statusError} />;
    case 'pending':
      return <Clock size={16} className={styles.statusPending} />;
    case 'extracting':
    case 'embedding':
      return <Loader size={16} className={`${styles.statusProcessing} ${styles.spin}`} />;
    default:
      return <Clock size={16} className={styles.statusPending} />;
  }
};

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const KnowledgeBase: React.FC<KnowledgeBaseProps> = ({
  assistantId = '',
  assistantName = 'Assistant',
}) => {
  const [datasources, setDatasources] = useState<Datasource[]>([]);
  const [stats, setStats] = useState<KnowledgeBaseStats | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedDatasource, setSelectedDatasource] = useState<Datasource | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDatasources = useCallback(async () => {
    if (!assistantId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.listDatasources(assistantId);
      setDatasources(response.data);
    } catch (err) {
      setError('Failed to load datasources');
      console.error('Failed to fetch datasources:', err);
    } finally {
      setIsLoading(false);
    }
  }, [assistantId]);

  const fetchStats = useCallback(async () => {
    if (!assistantId) return;
    try {
      const response = await api.getKnowledgeBaseStats(assistantId);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [assistantId]);

  useEffect(() => {
    fetchDatasources();
    fetchStats();
  }, [fetchDatasources, fetchStats]);

  const handleDeleteDatasource = async (datasourceId: string) => {
    if (!assistantId) return;
    if (!window.confirm('Are you sure you want to delete this datasource? This will remove all associated documents and vectors.')) {
      return;
    }

    try {
      await api.deleteDatasource(assistantId, datasourceId);
      setDatasources((prev) => prev.filter((d) => d.id !== datasourceId));
      fetchStats();
    } catch (err) {
      console.error('Failed to delete datasource:', err);
      setError('Failed to delete datasource');
    }
  };

  const handleUploadComplete = () => {
    setIsUploadModalOpen(false);
    setSelectedDatasource(null);
    fetchDatasources();
    fetchStats();
  };

  const filteredDatasources = datasources.filter((ds) =>
    ds.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <Database size={24} />
          <h2>Knowledge Base</h2>
          <span className={styles.assistantName}>for {assistantName}</span>
        </div>
        <div className={styles.headerActions}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              fetchDatasources();
              fetchStats();
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsUploadModalOpen(true)}
          >
            <Upload size={16} />
            Upload Document
          </Button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{stats.totalDocuments}</span>
            <span className={styles.statLabel}>Documents</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{stats.totalChunks}</span>
            <span className={styles.statLabel}>Chunks</span>
          </div>
          <div className={styles.statItem}>
            <span className={styles.statValue}>{datasources.length}</span>
            <span className={styles.statLabel}>Datasources</span>
          </div>
        </div>
      )}

      {/* Search */}
      <div className={styles.searchBar}>
        <div className={styles.searchInputWrapper}>
          <Search size={16} className={styles.searchIcon} />
          <Input
            placeholder="Search datasources..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className={styles.errorMessage}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className={styles.loadingState}>
          <Loader size={32} className={styles.spin} />
          <p>Loading knowledge base...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && datasources.length === 0 && (
        <div className={styles.emptyState}>
          <Database size={48} className={styles.emptyIcon} />
          <h3>No documents yet</h3>
          <p>Upload documents to build your assistant's knowledge base</p>
          <Button
            variant="primary"
            onClick={() => setIsUploadModalOpen(true)}
          >
            <Upload size={16} />
            Upload Your First Document
          </Button>
        </div>
      )}

      {/* Datasources List */}
      {!isLoading && filteredDatasources.length > 0 && (
        <div className={styles.datasourcesList}>
          {filteredDatasources.map((datasource) => (
            <div key={datasource.id} className={styles.datasourceCard}>
              <div className={styles.datasourceIcon}>
                {getFileIcon(datasource.metadata?.fileType)}
              </div>
              <div className={styles.datasourceInfo}>
                <div className={styles.datasourceName}>{datasource.name}</div>
                {datasource.description && (
                  <div className={styles.datasourceDescription}>
                    {datasource.description}
                  </div>
                )}
                <div className={styles.datasourceMeta}>
                  <span className={styles.metaItem}>
                    {getStatusIcon(datasource.status)}
                    {datasource.status}
                  </span>
                  {datasource.metadata?.fileSize && (
                    <span className={styles.metaItem}>
                      {formatFileSize(datasource.metadata.fileSize)}
                    </span>
                  )}
                  {datasource.metadata?.chunkCount && (
                    <span className={styles.metaItem}>
                      {datasource.metadata.chunkCount} chunks
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.datasourceActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedDatasource(datasource);
                    setIsUploadModalOpen(true);
                  }}
                  title="Add documents"
                >
                  <Upload size={16} />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteDatasource(datasource.id)}
                  className={styles.deleteButton}
                  title="Delete datasource"
                >
                  <Trash2 size={16} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setSelectedDatasource(null);
        }}
        assistantId={assistantId}
        existingDatasource={selectedDatasource}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

export default KnowledgeBase;
